import { RiCheckLine, RiErrorWarningLine, RiTimeLine } from "@remixicon/react";
import { useQuery } from "@tanstack/react-query";
import { fetchClient } from "@/api/client";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";

export interface SchedulerHealthRow {
	jobKind: string;
	organizationId: string | null;
	lastAttemptAt: string | null;
	lastSuccessAt: string | null;
	consecutiveFailures: number;
	lastError: string | null;
}

const QUERY_KEY = ["scheduler", "health"] as const;

/// Human labels for the job identifiers the backend records. An unknown key
/// falls back to the raw identifier rather than being hidden - a job missing
/// from this map must still be visible, since the whole point of the table is
/// surfacing work nobody is watching.
const JOB_LABELS: Record<string, string> = {
	prohandel_sync: "Prohandel sync",
	prohandel_size_upsert: "Prohandel stock rows (size upsert)",
	prohandel_scheduler_tick: "Prohandel scheduler (tick)",
	farfetch_catalogue_export: "Farfetch catalogue export",
	farfetch_stock_export: "Farfetch stock feed (IF1220)",
	farfetch_scheduler_tick: "Farfetch scheduler (tick)",
	farfetch_stock_delta_tick: "Farfetch stock deltas (IF1010)",
	farfetch_order_poll: "Farfetch order poll",
	farfetch_order_poll_tick: "Farfetch order poll (tick)",
	farfetch_result_poll: "Farfetch catalogue results poll",
	farfetch_result_poll_tick: "Farfetch result poll (tick)",
	catalogue_rebuild_tick: "Catalogue rebuild worker",
	store_offer_sweep_tick: "Store offer sweeper",
	prohandel_transfer_reconcile: "Prohandel transfer reconciliation",
	prohandel_sale_booking: "Prohandel sale booking",
	prohandel_sale_reconcile: "Prohandel sale reconciliation",
	prohandel_held_stock_reconcile: "Prohandel held-stock check",
	fx_rate_import: "ECB exchange rates",
	fx_secondary_import: "Secondary exchange rates (non-ECB currencies)",
};

export function formatRelative(iso: string | null, now: number = Date.now()): string {
	if (!iso) return "never";
	const then = new Date(iso).getTime();
	if (Number.isNaN(then)) return "never";
	const mins = Math.floor((now - then) / 60000);
	if (mins < 1) return "just now";
	if (mins < 60) return `${mins}m ago`;
	const hours = Math.floor(mins / 60);
	if (hours < 24) return `${hours}h ago`;
	return `${Math.floor(hours / 24)}d ago`;
}

/// Background-job health (DEV-777).
///
/// Every scheduler in this app used to swallow its failures into a log line, so
/// a Farfetch export that had been failing for days looked identical to one
/// that had simply not run yet. This reads the durable record.
export function SchedulerHealth() {
	const query = useQuery({
		queryKey: QUERY_KEY,
		queryFn: async () => {
			const { data, error } = await fetchClient.GET("/api/v1/scheduler/health" as never);
			if (error) throw new Error("failed to load scheduler health");
			return data as SchedulerHealthRow[];
		},
		// Cheap query, and an operator opening this card wants the current
		// state rather than whatever was cached when they last visited.
		refetchInterval: 30_000,
	});

	return (
		<Card>
			<CardHeader>
				<CardTitle>Background jobs</CardTitle>
				<CardDescription>
					Health of the scheduled syncs, exports and pollers. A failing job retries on its own
					interval; the failure count is how many attempts in a row have failed.
				</CardDescription>
			</CardHeader>
			<CardContent>
				{query.isLoading ? (
					<div className="space-y-2">
						<Skeleton className="h-8 w-full" />
						<Skeleton className="h-8 w-full" />
						<Skeleton className="h-8 w-full" />
					</div>
				) : query.isError ? (
					<p role="alert" className="text-sm text-destructive">
						Could not load background job health.
					</p>
				) : !query.data || query.data.length === 0 ? (
					<p className="text-sm text-muted-foreground">
						No jobs have reported yet. A row appears the first time each job runs.
					</p>
				) : (
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Job</TableHead>
								<TableHead>Status</TableHead>
								<TableHead>Last success</TableHead>
								<TableHead>Detail</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{query.data.map((row) => {
								const failing = row.consecutiveFailures > 0;
								return (
									<TableRow key={`${row.organizationId ?? "global"}:${row.jobKind}`}>
										<TableCell className="font-medium">
											{JOB_LABELS[row.jobKind] ?? row.jobKind}
										</TableCell>
										<TableCell>
											{failing ? (
												<Badge variant="destructive" className="gap-1">
													<RiErrorWarningLine className="size-3.5" />
													{row.consecutiveFailures} failed
												</Badge>
											) : (
												<Badge variant="secondary" className="gap-1">
													<RiCheckLine className="size-3.5" />
													OK
												</Badge>
											)}
										</TableCell>
										<TableCell className="whitespace-nowrap text-muted-foreground">
											<span className="inline-flex items-center gap-1">
												<RiTimeLine className="size-3.5" />
												{formatRelative(row.lastSuccessAt)}
											</span>
										</TableCell>
										<TableCell className="max-w-md text-sm text-muted-foreground">
											{failing && row.lastError ? (
												<span className="break-words">{row.lastError}</span>
											) : (
												"-"
											)}
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				)}
			</CardContent>
		</Card>
	);
}
